import React, {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useLanding } from "../context/LandingContext";
import { navbarOffsetSx, resolveNavbarStyle } from "../utils/landingLayout";
import { handleLandingMediaError, resolveLandingMedia } from "../utils/landingMedia";
import { openConfiguredUrl } from "../utils/configuredNavigation";
import { useProjectConfig } from "../context/ProjectConfigContext";
import {
  renderedTextExceedsLines,
  safeButtonTextSx,
  safeTextSx,
  safeTitleSx,
  sectionTitleFontSize,
  shouldCollapseText,
} from "../utils/textSafety";
import { scaleResponsiveCss, segmentVisualScale } from "../utils/segmentVisualStyle";
import { capitalizeSectionTitle } from "../utils/landingSectionText";

const DESCRIPTION_LINE_LIMIT = { xs: 7, md: 8 };
const DESCRIPTION_MAX_HEIGHT = {
  xs: `${1.85 * DESCRIPTION_LINE_LIMIT.xs}em`,
  md: `${1.85 * DESCRIPTION_LINE_LIMIT.md}em`,
};

function cssColorWithOpacity(color, opacity) {
  const raw = String(color ?? "").trim();
  const alpha = Math.max(0, Math.min(1, Number(opacity)));
  if (!raw || alpha <= 0) return "transparent";
  const hex = raw.match(/^#([0-9a-f]{6})$/i);
  if (hex) {
    const value = hex[1];
    return `rgba(${parseInt(value.slice(0, 2), 16)}, ${parseInt(value.slice(2, 4), 16)}, ${parseInt(value.slice(4, 6), 16)}, ${alpha})`;
  }
  return raw;
}

function explicitVisualShadow(style = {}, hover = false) {
  const prefix = hover ? "hoverShadow" : "shadow";
  const opacity = Number(style[`${prefix}Opacity`]);
  if (!Number.isFinite(opacity) || opacity <= 0) return "none";
  const x = Number.isFinite(Number(style[`${prefix}X`])) ? Number(style[`${prefix}X`]) : 0;
  const y = Number.isFinite(Number(style[`${prefix}Y`])) ? Number(style[`${prefix}Y`]) : 0;
  const blur = Number.isFinite(Number(style[`${prefix}Blur`])) ? Number(style[`${prefix}Blur`]) : 0;
  const spread = Number.isFinite(Number(style[`${prefix}Spread`])) ? Number(style[`${prefix}Spread`]) : 0;
  return `${x}px ${y}px ${blur}px ${spread}px ${cssColorWithOpacity(style[`${prefix}Color`] || "#000000", opacity)}`;
}

function LandingAboutSection({ sectionDomId = "about" }) {
  const navigate = useNavigate();
  const { about, theme, navigation, sections } = useLanding();
  const navbarOffset = navbarOffsetSx(
    resolveNavbarStyle(navigation, theme),
    sections?.showNavbar !== false
  );
  const { productsEnabled } = useProjectConfig();
  const imageUrl = resolveLandingMedia(about?.imageUrl);
  const description = String(about?.description ?? "");
  const descriptionId = useId();
  const descriptionRef = useRef(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionCanCollapse, setDescriptionCanCollapse] = useState(() =>
    shouldCollapseText(description)
  );
  const showCta = Boolean(about?.ctaLabel && about?.ctaUrl) &&
    (!about?.ctaRequiresProducts || productsEnabled);
  const hasCopy = Boolean(about?.eyebrow || about?.title || description || showCta);

  const measureDescription = useCallback(() => {
    const element = descriptionRef.current;
    if (!element) return;

    const computedStyle = window.getComputedStyle(element);
    const responsiveLineLimit = Number.parseInt(
      computedStyle.getPropertyValue("--wp-about-description-line-limit"),
      10
    );
    const lineLimit = Number.isFinite(responsiveLineLimit)
      ? responsiveLineLimit
      : DESCRIPTION_LINE_LIMIT.xs;

    setDescriptionCanCollapse(renderedTextExceedsLines(element, lineLimit));
  }, []);

  useLayoutEffect(() => {
    setDescriptionExpanded(false);
    setDescriptionCanCollapse(shouldCollapseText(description));

    if (!description || !descriptionRef.current) return undefined;

    let cancelled = false;
    const measure = () => {
      if (!cancelled) measureDescription();
    };

    measure();
    window.addEventListener("resize", measure);

    const resizeObserver =
      typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    resizeObserver?.observe(descriptionRef.current);

    if (document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [description, measureDescription]);

  const backgroundColor = about?.backgroundColor || "#EEF0F5";
  const surfaceColor = about?.contentBackgroundColor || "transparent";
  const hasSurfaceColor = surfaceColor !== "transparent";
  const accentColor = about?.eyebrowColor || "#F35A3A";
  const titleColor = about?.titleColor || "#19242F";
  const textColor = about?.descriptionColor || "#5B5D73";
  const descriptionToggleColor = about?.descriptionToggleColor || "#F35A3A";
  const contentBorderRadius = Number.isFinite(Number(about?.contentBorderRadius))
    ? Number(about.contentBorderRadius)
    : 40;
  const imageStyle = about?.imageStyle || {};
  const imageBackgroundColor = imageStyle.backgroundColor || "rgba(25,36,47,.06)";
  const imageBorderRadius = Number.isFinite(Number(imageStyle.borderRadius))
    ? Number(imageStyle.borderRadius)
    : 50;
  const imageShadow = explicitVisualShadow(imageStyle);
  const ctaStyle = about?.ctaStyle || {};
  const ctaBackgroundColor = ctaStyle.backgroundColor || "#F35A3A";
  const ctaTextColor = ctaStyle.textColor || "#FFFFFF";
  const ctaHoverBackgroundColor = ctaStyle.hoverBackgroundColor || "#E05335";
  const ctaHoverTextColor = ctaStyle.hoverTextColor || "#EBEBEB";
  const ctaBorderColor = ctaStyle.borderColor || "transparent";
  const ctaBorderWidth = Number.isFinite(Number(ctaStyle.borderWidth)) ? Number(ctaStyle.borderWidth) : 0;
  const ctaBorderRadius = Number.isFinite(Number(ctaStyle.borderRadius)) ? Number(ctaStyle.borderRadius) : 999;
  const ctaShadow = explicitVisualShadow(ctaStyle);
  const ctaHoverShadow = explicitVisualShadow(ctaStyle, true);
  const ctaFocusRingColor = ctaStyle.focusRingColor || "#F35A3A18";
  const ctaFocusShadow = ctaShadow === "none"
    ? `0 0 0 4px ${ctaFocusRingColor}`
    : `0 0 0 4px ${ctaFocusRingColor}, ${ctaShadow}`;
  const imageOnRight = about?.layout === "image-right";
  const titleScale = segmentVisualScale(theme, "about", "title");
  const imageScale = segmentVisualScale(theme, "about", "image");
  const spacingScale = segmentVisualScale(theme, "about", "spacing");
  const titleSize = scaleResponsiveCss(
    sectionTitleFontSize(about?.title, { xs: "2.15rem", md: "3rem" }),
    titleScale
  );

  if (!hasCopy && !imageUrl) return null;

  const go = () => {
    if (!about?.ctaUrl) return;
    openConfiguredUrl(about.ctaUrl, navigate);
  };

  return (
    <Box
      component="section"
      id={sectionDomId}
      data-landing-section="about"
      sx={{
        px: { xs: 2.5, md: 8 },
        py: { xs: 7 * spacingScale, md: 10 * spacingScale },
        background: backgroundColor,
        scrollMarginTop: navbarOffset,
      }}
    >
      <Box
        sx={{
          maxWidth: 1320,
          mx: "auto",
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: imageUrl && hasCopy
              ? "repeat(2, minmax(0, 1fr))"
              : "minmax(0, 1100px)",
          },
          gap: { xs: 4, sm: 5, md: 7 },
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {imageUrl ? (
          <Box
            sx={{
              minWidth: 0,
              width: "100%",
              order: { xs: 1, sm: imageOnRight ? 2 : 1 },
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={about?.imageAlt ?? ""}
              loading="lazy"
              decoding="async"
              onError={handleLandingMediaError}
              sx={{
                display: "block",
                width: "100%",
                aspectRatio: { xs: "4 / 3", md: "6 / 5" },
                minHeight: { xs: 300 * imageScale, md: 480 * imageScale },
                bgcolor: imageBackgroundColor,
                maxHeight: 560 * imageScale,
                objectFit: "cover",
                objectPosition: about?.imagePosition || "center",
                borderRadius: `${imageBorderRadius}px`,
                boxShadow: imageShadow,
              }}
            />
          </Box>
        ) : null}

        {hasCopy ? (
          <Box
            sx={{
              minWidth: 0,
              width: "100%",
              order: { xs: 2, sm: imageOnRight ? 1 : 2 },
              bgcolor: surfaceColor,
              borderRadius: hasSurfaceColor ? `${contentBorderRadius}px` : 0,
              p: hasSurfaceColor ? { xs: 3, md: 4 } : 0,
            }}
          >
            {about?.eyebrow ? (
              <Typography
                data-landing-field="about.eyebrow"
                variant="overline"
                sx={{
                  ...safeTextSx,
                  color: accentColor,
                  fontWeight: 900,
                  letterSpacing: ".16em",
                }}
              >
                {about.eyebrow}
              </Typography>
            ) : null}

            {about?.title ? (
              <Typography
                data-landing-field="about.title"
                variant="h3"
                sx={{
                  ...safeTitleSx,
                  mt: about?.eyebrow ? 1 : 0,
                  color: titleColor,
                  fontSize: titleSize,
                  fontWeight: "var(--wp-segment-title-weight)",
                  letterSpacing: "var(--wp-segment-letter-spacing)",
                  lineHeight: 1.08,
                }}
              >
                {capitalizeSectionTitle(about.title)}
              </Typography>
            ) : null}

            {description ? (
              <>
                <Typography
                  data-landing-field="about.description"
                  ref={descriptionRef}
                  id={descriptionId}
                  sx={{
                    ...safeTextSx,
                    "--wp-about-description-line-limit": DESCRIPTION_LINE_LIMIT,
                    mt: about?.title || about?.eyebrow ? 2.5 : 0,
                    color: textColor,
                    fontSize: "1.08rem",
                    lineHeight: 1.85,
                    whiteSpace: "pre-wrap",
                    ...(descriptionCanCollapse && !descriptionExpanded
                      ? {
                          maxHeight: DESCRIPTION_MAX_HEIGHT,
                          overflow: "hidden",
                        }
                      : {}),
                  }}
                >
                  {description}
                </Typography>

                {descriptionCanCollapse ? (
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    aria-expanded={descriptionExpanded}
                    aria-controls={descriptionId}
                    aria-label={
                      descriptionExpanded
                        ? "Mostrar menos de la descripción sobre el negocio"
                        : "Mostrar la descripción completa sobre el negocio"
                    }
                    onClick={() => setDescriptionExpanded((current) => !current)}
                    sx={{
                      ...safeButtonTextSx,
                      mt: 1.25,
                      ml: -1,
                      minWidth: 0,
                      color: descriptionToggleColor,
                      textTransform: "none",
                      fontWeight: 900,
                    }}
                  >
                    {descriptionExpanded ? "Ver menos" : "Ver más"}
                  </Button>
                ) : null}
              </>
            ) : null}

            {showCta ? (
              <Button
                data-landing-field="about.ctaLabel"
                onClick={go}
                variant="contained"
                sx={{
                  ...safeButtonTextSx,
                  mt: about?.eyebrow || about?.title || description ? 3.5 : 0,
                  maxWidth: "100%",
                  bgcolor: ctaBackgroundColor,
                  color: ctaTextColor,
                  border: `${ctaBorderWidth}px solid ${ctaBorderColor}`,
                  borderRadius: `${ctaBorderRadius}px`,
                  boxShadow: ctaShadow,
                  filter: "none",
                  px: 4,
                  py: 1.3,
                  textTransform: "none",
                  fontWeight: 900,
                  "&:hover": {
                    bgcolor: ctaHoverBackgroundColor,
                    color: ctaHoverTextColor,
                    boxShadow: ctaHoverShadow,
                    filter: "none",
                    transform: "translateY(-1px)",
                  },
                  "&:focus-visible": {
                    boxShadow: ctaFocusShadow,
                  },
                  "&:active": { transform: "scale(.98)" },
                }}
              >
                {about.ctaLabel}
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}


export default LandingAboutSection;
